import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Home,
  Building2,
  Hammer,
  Sparkles,
  Store,
  CalendarCheck,
  CheckCircle2,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  ArrowRight,
  Star,
  ShieldCheck,
  Clock,
  Leaf,
  FileText,
  Users,
  Heart,
  PhoneCall,
  ClipboardCheck,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Logo from "@/components/Logo";
import EuLogoBar from "@/components/EuLogoBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV = [
  { id: "hero", label: "Strona główna" },
  { id: "uslugi", label: "Usługi" },
  { id: "o-nas", label: "O nas" },
  { id: "dlaczego-my", label: "Dlaczego my" },
  { id: "kontakt", label: "Kontakt" },
];

const SERVICES = [
  {
    icon: Home,
    title: "Sprzątanie mieszkań i domów",
    desc: "Kompleksowe sprzątanie przestrzeni mieszkalnych — od kuchni po sypialnie.",
  },
  {
    icon: Building2,
    title: "Sprzątanie biur",
    desc: "Czyste i przyjemne miejsce pracy, które robi wrażenie na klientach.",
  },
  {
    icon: Hammer,
    title: "Sprzątanie po remontach",
    desc: "Usuwamy pył, resztki i zabrudzenia, zostawiając wnętrze gotowe do życia.",
  },
  {
    icon: Sparkles,
    title: "Mycie okien",
    desc: "Krystalicznie czyste szyby bez smug — więcej światła w Twoim wnętrzu.",
  },
  {
    icon: Store,
    title: "Sprzątanie lokali usługowych",
    desc: "Sklepy, gabinety i punkty usługowe w nienagannej czystości.",
  },
  {
    icon: CalendarCheck,
    title: "Regularna opieka nad czystością",
    desc: "Stałe, cykliczne sprzątanie dopasowane do Twojego harmonogramu.",
  },
];

const BENEFITS = [
  { icon: ShieldCheck, title: "Dokładność", desc: "Dbamy o każdy detal — od narożników po trudno dostępne miejsca." },
  { icon: Clock, title: "Elastyczne terminy", desc: "Dopasowujemy się do Twojego kalendarza, także w weekendy." },
  { icon: Leaf, title: "Profesjonalne środki", desc: "Skuteczne i bezpieczne preparaty, przyjazne dla domowników." },
  { icon: FileText, title: "Indywidualna wycena", desc: "Płacisz za realny zakres pracy — bez ukrytych kosztów." },
  { icon: Users, title: "Klienci prywatni i firmy", desc: "Obsługujemy zarówno mieszkania, jak i przestrzenie biznesowe." },
  { icon: Heart, title: "Miła i rzetelna obsługa", desc: "Uśmiech, punktualność i pełne zaufanie na każdym etapie." },
];

const STEPS = [
  { icon: PhoneCall, title: "Kontakt", desc: "Napisz lub zadzwoń — opowiedz nam o swoich potrzebach." },
  { icon: ClipboardCheck, title: "Darmowa wycena", desc: "Przygotujemy bezpłatną, indywidualną wycenę usługi." },
  { icon: Sparkles, title: "Sprzątanie w terminie", desc: "Realizujemy sprzątanie dokładnie w ustalonym czasie." },
];

const TESTIMONIALS = [
  {
    name: "Anna Kowalska",
    role: "Klientka prywatna",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80&auto=format&fit=crop",
    text: "Mieszkanie lśni czystością po każdej wizycie. Ekipa punktualna, dokładna i naprawdę miła. Polecam z całego serca!",
  },
  {
    name: "Marek Nowak",
    role: "Właściciel biura",
    avatar: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=200&q=80&auto=format&fit=crop",
    text: "Regularnie sprzątają nasze biuro. Zawsze terminowo, profesjonalnie i bez zarzutu. Współpraca to czysta przyjemność.",
  },
  {
    name: "Katarzyna Wiśniewska",
    role: "Klientka po remoncie",
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&q=80&auto=format&fit=crop",
    text: "Sprzątanie po remoncie wykonane perfekcyjnie. Ani śladu pyłu! Indywidualne podejście i uczciwa wycena.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  else window.location.href = `/#${id}`;
};

/* ---------------- Header ---------------- */
const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-100"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
        <button onClick={() => scrollTo("hero")} data-testid="logo-home-button" className="shrink-0">
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => scrollTo(n.id)}
              data-testid={`nav-${n.id}`}
              className="text-[15px] font-medium text-slate-600 hover:text-cyan-600 transition-colors relative group"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-cyan-400 group-hover:w-full transition-[width] duration-300" />
            </button>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button
            data-testid="header-cta-button"
            onClick={() => scrollTo("kontakt")}
            className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold px-6 h-11 shadow-lg shadow-cyan-500/25 hover:-translate-y-0.5 transition-transform"
          >
            Zamów wycenę
          </Button>
        </div>

        <button
          className="lg:hidden text-cyan-950 p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-5 py-4 space-y-1" data-testid="mobile-menu">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => {
                scrollTo(n.id);
                setOpen(false);
              }}
              data-testid={`mobile-nav-${n.id}`}
              className="block w-full text-left py-3 text-base font-medium text-slate-700 hover:text-cyan-600"
            >
              {n.label}
            </button>
          ))}
          <Button
            data-testid="mobile-cta-button"
            onClick={() => {
              scrollTo("kontakt");
              setOpen(false);
            }}
            className="w-full mt-2 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold h-12"
          >
            Zamów wycenę
          </Button>
        </div>
      )}
    </header>
  );
};

/* ---------------- Hero ---------------- */
const Hero = () => (
  <section id="hero" className="relative overflow-hidden" data-testid="hero-section">
    <div className="absolute top-0 right-0 w-[45%] h-full bg-sky-50 -z-10 hidden lg:block rounded-bl-[120px]" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <motion.div initial="hidden" animate="visible" variants={fadeUp}>
        <span className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-600 text-sm font-semibold px-4 py-1.5 rounded-full">
          <Sparkles size={16} /> Czystość na najwyższym poziomie
        </span>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cyan-950 mt-6 leading-[1.08]">
          Lusi Błysk —{" "}
          <span className="text-cyan-500">czystość, którą widać od razu</span>
        </h1>
        <p className="text-slate-600 text-lg leading-relaxed mt-6 max-w-xl">
          Profesjonalne sprzątanie mieszkań, domów, biur i lokali usługowych.
          Dokładnie, terminowo i z dbałością o każdy szczegół.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 mt-9">
          <Button
            data-testid="hero-contact-button"
            onClick={() => scrollTo("kontakt")}
            className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold h-13 px-8 py-6 text-base shadow-lg shadow-cyan-500/25 hover:-translate-y-0.5 transition-transform"
          >
            Skontaktuj się <ArrowRight size={18} className="ml-1" />
          </Button>
          <Button
            data-testid="hero-services-button"
            onClick={() => scrollTo("uslugi")}
            variant="outline"
            className="rounded-full border-2 border-cyan-200 text-cyan-700 hover:bg-cyan-50 hover:text-cyan-800 font-semibold h-13 px-8 py-6 text-base"
          >
            Zobacz usługi
          </Button>
        </div>
        {/*<div className="flex items-center gap-6 mt-10">
          <div className="flex -space-x-3">
            {TESTIMONIALS.map((t) => (
              <img
                key={t.name}
                src={t.avatar}
                alt={t.name}
                className="w-10 h-10 rounded-full border-2 border-white object-cover"
              />
            ))}
          </div>
          <div>
            <div className="flex text-amber-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} fill="currentColor" />
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">Zadowoleni klienci w całej Polsce</p>
          </div>
        </div>*/}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
      >
        <div className="rounded-[32px] overflow-hidden shadow-[0_20px_60px_rgb(6,182,212,0.18)]">
          <img
            src="https://images.pexels.com/photos/8134772/pexels-photo-8134772.jpeg?auto=compress&cs=tinysrgb&w=1200"
            alt="Lśniąco czyste, jasne wnętrze"
            className="w-full h-[380px] lg:h-[520px] object-cover"
          />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="absolute -bottom-6 -left-2 sm:left-6 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3 border border-slate-100"
        >
          <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="text-emerald-500" size={24} />
          </div>
          <div>
            <p className="font-heading font-bold text-cyan-950 leading-tight">100% dokładnie</p>
            <p className="text-xs text-slate-500">Gwarancja czystości</p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ---------------- Section wrapper ---------------- */
const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    variants={fadeUp}
    className="max-w-2xl"
  >
    <span className="text-cyan-500 font-semibold uppercase tracking-[0.15em] text-sm">{eyebrow}</span>
    <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-cyan-950 mt-3">
      {title}
    </h2>
    {subtitle && <p className="text-slate-600 text-lg leading-relaxed mt-4">{subtitle}</p>}
  </motion.div>
);

/* ---------------- Services ---------------- */
const Services = () => (
  <section id="uslugi" className="py-24 bg-white" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="Nasze usługi"
        title="Sprzątamy dokładnie to, czego potrzebujesz"
        subtitle="Kompleksowa oferta sprzątania dla domów, firm i lokali usługowych."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.title}
            custom={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            data-testid={`service-card-${i}`}
            className="group bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_16px_44px_rgb(6,182,212,0.14)] hover:-translate-y-1.5 transition-[transform,box-shadow] duration-300"
          >
            <div className="w-14 h-14 rounded-2xl bg-sky-50 group-hover:bg-cyan-500 flex items-center justify-center transition-colors duration-300">
              <s.icon className="text-cyan-500 group-hover:text-white transition-colors duration-300" size={28} />
            </div>
            <h3 className="font-heading font-bold text-xl text-cyan-950 mt-6">{s.title}</h3>
            <p className="text-slate-600 leading-relaxed mt-3">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- About ---------------- */
const About = () => (
  <section id="o-nas" className="py-24 bg-sky-50" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="relative"
      >
        <div className="rounded-[32px] overflow-hidden shadow-xl">
          <img
            src="https://images.pexels.com/photos/9462192/pexels-photo-9462192.jpeg?auto=compress&cs=tinysrgb&w=1000"
            alt="Profesjonalna ekipa sprzątająca"
            className="w-full h-[460px] object-cover"
          />
        </div>
        <div className="absolute -top-5 -right-3 sm:right-6 bg-cyan-500 text-white rounded-2xl px-6 py-4 shadow-lg">
          <p className="font-heading font-black text-3xl">10+</p>
          <p className="text-sm text-cyan-50">lat doświadczenia</p>
        </div>
      </motion.div>

      <div>
        <SectionHeading
          eyebrow="O nas"
          title="Firma, której możesz zaufać"
          subtitle="Lusi Błysk to zespół, dla którego czystość to pasja i codzienny standard. Łączymy dokładność, terminowość oraz indywidualne podejście do każdego klienta."
        />
        <div className="space-y-4 mt-8">
          {[
            "Dokładność, którą widać w każdym detalu.",
            "Terminowość i pełne poszanowanie Twojego czasu.",
            "Indywidualne podejście dostosowane do Twoich potrzeb.",
          ].map((t) => (
            <div key={t} className="flex items-start gap-3">
              <CheckCircle2 className="text-emerald-500 shrink-0 mt-0.5" size={22} />
              <span className="text-slate-700 text-lg">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Why us ---------------- */
const WhyUs = () => (
  <section id="dlaczego-my" className="py-24 bg-white" data-testid="why-us-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading
        eyebrow="Dlaczego my"
        title="Dlaczego warto wybrać Lusi Błysk"
        subtitle="Konkretne powody, dla których klienci wracają do nas i polecają nas dalej."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
        {BENEFITS.map((b, i) => (
          <motion.div
            key={b.title}
            custom={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            data-testid={`benefit-card-${i}`}
            className="flex gap-4 p-7 rounded-3xl bg-gradient-to-br from-sky-50 to-white border border-slate-100 hover:border-cyan-200 transition-colors"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <b.icon className="text-emerald-600" size={24} />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg text-cyan-950">{b.title}</h3>
              <p className="text-slate-600 leading-relaxed mt-1.5">{b.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Process ---------------- */
const Process = () => (
  <section className="py-24 bg-cyan-950 relative overflow-hidden" data-testid="process-section">
    <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-cyan-500/10" />
    <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-emerald-500/10" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
      <div className="max-w-2xl">
        <span className="text-cyan-300 font-semibold uppercase tracking-[0.15em] text-sm">Proces współpracy</span>
        <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-white mt-3">
          Trzy proste kroki do czystości
        </h2>
      </div>
      <div className="grid md:grid-cols-3 gap-8 mt-14">
        {STEPS.map((s, i) => (
          <motion.div
            key={s.title}
            custom={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            data-testid={`process-step-${i}`}
            className="relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10"
          >
            <div className="flex items-center justify-between">
              <div className="w-14 h-14 rounded-2xl bg-cyan-500 flex items-center justify-center">
                <s.icon className="text-white" size={26} />
              </div>
              <span className="font-heading font-black text-5xl text-white/10">0{i + 1}</span>
            </div>
            <h3 className="font-heading font-bold text-xl text-white mt-6">{s.title}</h3>
            <p className="text-cyan-100/70 leading-relaxed mt-3">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Testimonials ---------------- */
const Testimonials = () => (
  <section className="py-24 bg-sky-50" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <SectionHeading eyebrow="Opinie klientów" title="Co mówią o nas klienci" />
      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {TESTIMONIALS.map((t, i) => (
          <motion.div
            key={t.name}
            custom={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            data-testid={`testimonial-card-${i}`}
            className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-slate-100"
          >
            <div className="flex text-amber-400 mb-4">
              {[...Array(5)].map((_, k) => (
                <Star key={k} size={18} fill="currentColor" />
              ))}
            </div>
            <p className="text-slate-700 leading-relaxed italic">„{t.text}”</p>
            <div className="flex items-center gap-3 mt-6 pt-6 border-t border-slate-100">
              <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
              <div>
                <p className="font-heading font-bold text-cyan-950">{t.name}</p>
                <p className="text-sm text-slate-500">{t.role}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Contact ---------------- */
const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Uzupełnij wymagane pola.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Dziękujemy! Odezwiemy się wkrótce z bezpłatną wyceną.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Coś poszło nie tak. Spróbuj ponownie lub zadzwoń do nas.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="py-24 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <SectionHeading
            eyebrow="Kontakt"
            title="Zamów bezpłatną wycenę"
            subtitle="Wypełnij formularz lub skontaktuj się z nami bezpośrednio. Odpowiadamy szybko i konkretnie."
          />
          <div className="space-y-5 mt-10">
            {[
              { icon: Phone, label: "Telefon", value: "+48 536 262 259" },
              { icon: Mail, label: "E-mail", value: "kontakt@lusiiblysk.pl" },
              { icon: MapPin, label: "Lokalizacja", value: "34-350 Żabnica, Polska" },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-4" data-testid={`contact-info-${c.label.toLowerCase()}`}>
                <div className="w-12 h-12 rounded-2xl bg-sky-50 flex items-center justify-center">
                  <c.icon className="text-cyan-500" size={22} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{c.label}</p>
                  <p className="font-heading font-semibold text-cyan-950">{c.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          onSubmit={submit}
          data-testid="contact-form"
          className="bg-sky-50/60 rounded-[32px] p-7 sm:p-9 border border-slate-100 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-cyan-950 font-medium">Imię i nazwisko *</Label>
              <Input
                id="name"
                data-testid="input-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Jan Kowalski"
                className="bg-white rounded-xl h-12 border-slate-200 focus:ring-2 focus:ring-cyan-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-cyan-950 font-medium">Telefon *</Label>
              <Input
                id="phone"
                data-testid="input-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+48 600 000 000"
                className="bg-white rounded-xl h-12 border-slate-200 focus:ring-2 focus:ring-cyan-400"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-cyan-950 font-medium">E-mail *</Label>
            <Input
              id="email"
              type="email"
              data-testid="input-email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="jan@example.com"
              className="bg-white rounded-xl h-12 border-slate-200 focus:ring-2 focus:ring-cyan-400"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-cyan-950 font-medium">Rodzaj usługi</Label>
            <Select value={form.service} onValueChange={(v) => update("service", v)}>
              <SelectTrigger data-testid="select-service" className="bg-white rounded-xl h-12 border-slate-200">
                <SelectValue placeholder="Wybierz usługę" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s.title} value={s.title} data-testid={`service-option-${s.title}`}>
                    {s.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-cyan-950 font-medium">Wiadomość *</Label>
            <Textarea
              id="message"
              data-testid="input-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Opisz, czego potrzebujesz..."
              rows={4}
              className="bg-white rounded-xl border-slate-200 focus:ring-2 focus:ring-cyan-400 resize-none"
            />
          </div>

          <Button
            type="submit"
            data-testid="contact-submit-button"
            disabled={loading}
            className="w-full rounded-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold h-13 py-6 text-base shadow-lg shadow-cyan-500/25 hover:-translate-y-0.5 transition-transform disabled:opacity-60"
          >
            {loading ? "Wysyłanie..." : <>Wyślij zapytanie <Send size={18} className="ml-1" /></>}
          </Button>
        </motion.form>
      </div>
    </section>
  );
};

/* ---------------- Footer ---------------- */
export const Footer = () => (
  <footer className="bg-cyan-950 text-white pt-16 pb-8" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid md:grid-cols-3 gap-10">
        <div>
          <Logo light />
          <p className="text-cyan-100/70 leading-relaxed mt-5 max-w-sm">
            Profesjonalne sprzątanie mieszkań, domów, biur i lokali usługowych.
            Czystość, którą widać od razu.
          </p>
        </div>
        <div>
          <h4 className="font-heading font-bold text-lg mb-4">Nawigacja</h4>
          <ul className="space-y-2.5">
            {NAV.map((n) => (
              <li key={n.id}>
                <button
                  onClick={() => scrollTo(n.id)}
                  data-testid={`footer-nav-${n.id}`}
                  className="text-cyan-100/70 hover:text-cyan-300 transition-colors"
                >
                  {n.label}
                </button>
              </li>
            ))}
            <li>
              <Link
                to="/dofinansowanie"
                data-testid="footer-nav-dofinansowanie"
                className="text-cyan-100/70 hover:text-cyan-300 transition-colors"
              >
                Dofinansowanie
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="font-heading font-bold text-lg mb-4">Kontakt</h4>
          <ul className="space-y-3 text-cyan-100/70">
            <li className="flex items-center gap-3"><Phone size={18} className="text-cyan-400" /> +48 536 262 259</li>
            <li className="flex items-center gap-3"><Mail size={18} className="text-cyan-400" /> kontakt@lusiiblysk.pl</li>
            <li className="flex items-center gap-3"><MapPin size={18} className="text-cyan-400" /> 34-350 Żabnica, Polska</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10 mt-12 pt-8" data-testid="footer-eu-info">
        <p className="text-cyan-100/70 text-sm leading-relaxed max-w-4xl">
          Strona internetowa firmy Lusi Błysk została wykonana w ramach projektu
          współfinansowanego ze środków Unii Europejskiej w ramach programu Fundusze
          Europejskie dla Śląskiego 2021–2027, Europejski Fundusz Społeczny Plus.
        </p>
        <p className="text-cyan-300 text-sm font-medium mt-2">#FunduszeUE #FunduszeEuropejskie</p>
      </div>

      <div className="border-t border-white/10 mt-8 pt-6 text-center text-cyan-100/60 text-sm">
        © 2026 Lusi Błysk. Wszystkie prawa zastrzeżone.
      </div>
    </div>
  </footer>
);

/* ---------------- Page ---------------- */
export default function LandingPage() {
  return (
    <div className="bg-white">
      <EuLogoBar />
      <Header />
      <main>
        <Hero />
        <Services />
        <About />
        <WhyUs />
        <Process />
        {/* <Testimonials /> */}
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
